import React from 'react';
import {
  Box,
  Button,
  TextField,
  Stack,
  Menu,
  InputAdornment,
  IconButton,
  Typography
} from '@mui/material';
import { getTranslation } from '../common';
import { Search } from '@mui/icons-material';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { ProcessModelType } from '../model/processModel';
import { Task } from '../model/activitiesModel';
import { Priority } from '../hooks/usePriorities';
import ProcessModelFilter from './ProccessModelFilter/ProcessModelFilter';
import ActivityFilter from './activity/ActivityFilter';
import LabelSearchSelect from './labels/LabelSearchSelect';
import PrioritySearchSelect from './priority/PrioritySearchSelect';
import { Translations } from '../model/messageModel';
import { ServerTag } from '../model/activitiesModel';

interface MyActivitiesFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  translations: Translations;
  searchText: string;
  setSearchText: (text: string) => void;
  handleClearFilters: (disableLocalStorage?: boolean, clearUserData?: boolean) => void;
  processModels: ProcessModelType[];
  selectedModels: number[];
  setSelectedModels: (models: number[]) => void;
  tasks: { todo: Task[], doing: Task[] };
  uniqueActivities: string[];
  selectedActivities: string[];
  setSelectedActivities: (activities: string[]) => void;
  displayedTags: ServerTag[];
  searchTags: number[];
  setSearchTags: React.Dispatch<React.SetStateAction<number[]>>;
  filteredTags: ServerTag[];
  showAllTags: boolean;
  setShowAllTags: React.Dispatch<React.SetStateAction<boolean>>;
  priorities: Priority[];
  selectedPriorities: string[];
  setSelectedPriorities: React.Dispatch<React.SetStateAction<string[]>>;
  userDataKeys: string[];
  selectedUserDataKey: string | null;
  handleSelectUserDataKey: (key: string | null) => void;
}

const MyActivitiesFilterMenu: React.FC<MyActivitiesFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  translations,
  searchText,
  setSearchText,
  handleClearFilters,
  processModels,
  selectedModels,
  setSelectedModels,
  tasks,
  uniqueActivities,
  selectedActivities,
  setSelectedActivities,
  displayedTags,
  searchTags,
  setSearchTags,
  filteredTags,
  showAllTags,
  setShowAllTags,
  priorities,
  selectedPriorities,
  setSelectedPriorities,
  userDataKeys,
  selectedUserDataKey,
  handleSelectUserDataKey,
}) => {
  return (
    <Menu
      id="filter-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box p={2} sx={{ overflowX: "hidden" }}>
        <Stack direction="column" spacing={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <TextField
              onKeyDown={(e) => e.stopPropagation()}
              id="search-tasks-text"
              variant="outlined"
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
              label={getTranslation(
                translations,
                "pm.organizationchart.ricerca.placeholder"
              )}
              value={searchText}
              onChange={(e) => {
                e.preventDefault();
                setSearchText(e.target.value);
              }}
            />
            <IconButton
              color="primary"
              onClick={() => handleClearFilters()}
            >
              <FilterListOffIcon />
            </IconButton>
          </Box>
          <Box minWidth={200}>
            {processModels.length > 0 && (
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
              >
                {getTranslation(
                  translations,
                  "generic.filter.by.processmodel"
                )}
              </Typography>
            )}
            <ProcessModelFilter
              processModelsId={selectedModels}
              onProcessModelsChange={setSelectedModels}
              options={processModels.filter((model) =>
                tasks.todo
                  .concat(tasks.doing)
                  .some((task) => task.idprocessmodel === model.id)
              )}
            />
          </Box>
          <Box minWidth={200}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="bold"
            >
              {getTranslation(
                translations,
                "generic.filter.by.activity"
              )}
            </Typography>
            <ActivityFilter
              selectedActivities={selectedActivities}
              onActivityChange={setSelectedActivities}
              options={uniqueActivities}
            />
          </Box>
          <Box minWidth={200}>
            {displayedTags.length > 0 && (
              <Typography
                variant="subtitle2"
                gutterBottom
                fontWeight="bold"
              >
                {getTranslation(
                  translations,
                  "generic.filter.by.tag"
                )}
              </Typography>
            )}
            <LabelSearchSelect
              tagList={displayedTags}
              idtags={searchTags}
              setSearchTags={setSearchTags}
            />
            {filteredTags.length > 5 && (
              <Button
                onClick={() => setShowAllTags((prev: any) => !prev)}
                variant="text"
                sx={{ mt: 1, ml: 4 }}
              >
                {showAllTags
                  ? getTranslation(
                    translations,
                    "generic.show.less"
                  )
                  : getTranslation(
                    translations,
                    "generic.show.all"
                  )}
              </Button>
            )}
          </Box>
          <Box minWidth={200}>
            {priorities.length > 0 && (
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
              >
                {getTranslation(
                  translations,
                  "generic.filter.by.priority"
                )}
              </Typography>
            )}
            <PrioritySearchSelect
              priorities={priorities}
              selectedPriorities={selectedPriorities}
              setSelectedPriorities={setSelectedPriorities}
            />
          </Box>
        </Stack>
      </Box>
    </Menu>
  );
};

export default MyActivitiesFilterMenu;
